<template>
  <div ref="teamsProjectsSection" :class="[CARD_COLORS.background, CARD_COLORS.border, 'rounded-xl shadow-sm border overflow-hidden']" role="region" :aria-label="t('dashboard.teamsProjects')">
    <!-- Header -->
    <div :class="[CARD_COLORS.border, BG_COLORS.secondary, 'px-6 py-2 border-b']">
      <div class="flex items-center justify-between">
        <h5 :class="['flex items-center text-xl m-0.5', titleClass]">
          <Icon name="teamsProjects" class="w-5 h-5 mr-2" :class="TEXT_COLORS.primary.combined" />
          {{ t('dashboard.teamsProjects') }}
        </h5>
        <div class="flex items-center space-x-3">
          <HLButton
            v-if="canManage"
            @click="$emit('manage-teams')"
            color="blue"
            variant="primary"
            size="sm"
            :aria-label="t('dashboard.manageTeamsProjects')"
          >
            <template #iconLeft>
              <Icon name="teamManagement" class="w-4 h-4" />
            </template>
            {{ t('dashboard.manageTeamsProjects') }}
          </HLButton>
        </div>
      </div>
    </div>
    
    <div class="p-6">
      <!-- Loading State -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-3 gap-6" role="status" aria-live="polite" :aria-label="t('common.loading')">
        <div class="md:col-span-1 space-y-3">
          <div v-for="i in 4" :key="i" :class="[BG_COLORS.secondary, 'h-12 rounded animate-pulse']"></div>
        </div>
        <div class="md:col-span-2 space-y-3">
          <div v-for="i in 3" :key="i" :class="[BG_COLORS.secondary, 'h-20 rounded animate-pulse']"></div>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="teams.length === 0" class="py-12 flex flex-col items-center justify-center" role="status" aria-live="polite">
        <HLEmpty 
          :description="canManage ? t('dashboard.noTeamsDescription') : t('dashboard.noTeamsDescriptionUser')"
        >
          <template #image>
            <Icon name="team" size="2xl" class="text-gray-400" />
          </template>
        </HLEmpty>
        <div v-if="canManage" class="mt-6">
          <HLButton
            @click="$emit('manage-teams')"
            color="blue"
            variant="primary"
            size="lg"
            :aria-label="t('dashboard.createTeam')"
          >
            <template #iconLeft>
              <Icon name="plus" class="w-4 h-4" />
            </template>
            {{ t('dashboard.createTeam') }}
          </HLButton>
        </div>
      </div>
      
      <!-- Two-Column Layout: Teams (LHS) and Projects (RHS) -->
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Left Side: Teams List -->
        <div class="md:col-span-1" ref="teamsContainer">
          <HLSpace vertical :size="12" class="w-full">
            <TeamCard
              v-for="team in sortedTeams"
              :key="team.id"
              :team="team"
              :selected="selectedTeamId === team.id"
              @click="handleTeamClick(team.id)"
            />
          </HLSpace>
        </div>
        
        <!-- Right Side: Projects List (scrollable if taller than teams) -->
        <div class="md:col-span-2">
          <div v-if="!selectedTeamId" class="flex items-center justify-center h-full min-h-[300px]" role="status">
            <HLEmpty :description="t('dashboard.selectTeam')">
              <template #image>
                <Icon name="comment" size="2xl" class="text-gray-400" />
              </template>
            </HLEmpty>
          </div>
          
          <div v-else class="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500" :style="{ maxHeight: teamsContainerHeight ? teamsContainerHeight + 'px' : 'auto' }">
            <div v-if="!selectedTeam?.projects || selectedTeam.projects.length === 0" class="py-8 flex flex-col items-center" role="status">
              <HLEmpty 
                :description="canManage ? t('dashboard.noProjectsDescription') : t('dashboard.noProjectsDescriptionUser')"
              >
                <template #image>
                  <Icon name="project" size="2xl" class="text-gray-400" />
                </template>
              </HLEmpty>
              <div class="mt-6">
                <HLButton
                  @click="$emit('create-project', selectedTeamId)"
                  color="blue"
                  variant="primary"
                  size="md"
                  :aria-label="t('dashboard.createProject')"
                >
                  <template #iconLeft>
                    <Icon name="plus" class="w-4 h-4" />
                  </template>
                  {{ t('dashboard.createProject') }}
                </HLButton>
              </div>
            </div>
            
            <HLSpace v-else vertical :size="12" class="w-full">
              <ProjectCard
                v-for="project in selectedTeam.projects"
                :key="project.id"
                :project="project"
              />
            </HLSpace>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import TeamCard from '@/components/dashboard/TeamCard.vue'
import ProjectCard from '@/components/dashboard/ProjectCard.vue'
import { HLButton, HLSpace, HLEmpty } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import { CARD_COLORS, BG_COLORS, TEXT_COLORS, EMPTY_STATE_COLORS } from '@/constants/uiColors'
import { t } from '@/i18n/en-us'

const props = defineProps({
  teams: {
    type: Array,
    required: true,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  selectedTeamId: {
    type: [String, Number, null],
    default: null
  },
  canManage: {
    type: Boolean,
    default: false
  },
  titleClass: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:selectedTeamId', 'manage-teams', 'create-project'])

// Refs
const teamsProjectsSection = ref(null)
const teamsContainer = ref(null)
const teamsContainerHeight = ref(null)

// Computed
const sortedTeams = computed(() => {
  // Sort teams by project count (descending) - teams with more projects appear first
  return [...props.teams].sort((a, b) => {
    const aProjects = a.projects?.length || 0
    const bProjects = b.projects?.length || 0
    return bProjects - aProjects
  })
})

const selectedTeam = computed(() => {
  return props.teams.find(team => team.id === props.selectedTeamId)
})

// Methods
const handleTeamClick = (teamId) => {
  emit('update:selectedTeamId', teamId)
}

const updateTeamsContainerHeight = () => {
  if (teamsContainer.value) {
    teamsContainerHeight.value = teamsContainer.value.offsetHeight
  }
}

// Watch sortedTeams to update container height
watch(sortedTeams, () => {
  nextTick(() => {
    updateTeamsContainerHeight()
  })
}, { flush: 'post' })

// Watch teams prop changes to update height
watch(() => props.teams, () => {
  nextTick(() => {
    updateTeamsContainerHeight()
  })
}, { flush: 'post' })

// Expose ref for parent to scroll to
defineExpose({
  teamsProjectsSection
})
</script>

